import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock, Calendar, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import {
  CATEGORIES,
  getPublishedPosts,
  getFeaturedPost,
  getCategoryByKey,
} from '@/data/blog/posts';

const formatDate = (iso) => {
  // Parse YYYY-MM-DD as a local date to avoid UTC→local day-shift.
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BlogPage = () => {
  const featured = getFeaturedPost();
  const allPosts = getPublishedPosts();
  const [activeCat, setActiveCat] = useState('all');

  const nonFeaturedPosts = useMemo(
    () => allPosts.filter((p) => p.slug !== featured?.slug),
    [allPosts, featured]
  );
  const hasMorePosts = nonFeaturedPosts.length > 0;

  const otherPosts = useMemo(() => {
    if (activeCat === 'all') return nonFeaturedPosts;
    return nonFeaturedPosts.filter((p) => p.category === activeCat);
  }, [nonFeaturedPosts, activeCat]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'The Navs Journal',
    url: 'https://www.navsartstudio.com/blog',
    description:
      'Inspiration, home décor ideas, personalized gift ideas, and stories behind handmade artwork by Navs Art Studio.',
    publisher: {
      '@type': 'Organization',
      name: 'Navs Art Studio',
    },
    blogPost: allPosts.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: `https://www.navsartstudio.com/blog/${p.slug}`,
        datePublished: p.publishedAt,
        author: { '@type': 'Organization', name: p.author || 'Navs Art Studio' },
      })),
  };

  return (
    <div
      className="min-h-screen pt-28 pb-24"
      style={{
        background:
          'linear-gradient(180deg, #FAF7F1 0%, #F1EEE7 40%, #E8EEEF 100%)',
      }}
      data-testid="blog-page"
    >
      <SEO
        title="The Navs Journal — Art, Décor & Custom Stories"
        description="Inspiration, home décor ideas, personalized gift ideas, and the stories behind handmade resin and textured artwork from Navs Art Studio in Vancouver."
        path="/blog"
        jsonLd={jsonLd}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16 sm:mb-20"
        >
          <p
            className="text-[11px] uppercase tracking-[0.5em] mb-5 flex items-center justify-center gap-2"
            style={{ color: '#0B1F26' }}
          >
            <Sparkles size={12} className="text-[#D4AF5A]" />
            <span>The Navs Journal</span>
            <Sparkles size={12} className="text-[#D4AF5A]" />
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 tracking-[-0.01em]"
            style={{ fontFamily: 'Playfair Display, serif', color: '#0B1F26', fontWeight: 500 }}
            data-testid="blog-title"
          >
            Stories, Ideas <em className="italic" style={{ color: '#1E4A55' }}>& Inspiration</em>
          </h1>
          <p className="text-[#3F4F55] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Inspiration, home décor ideas, personalized gift ideas, and stories behind handmade artwork.
          </p>
        </motion.div>

        {/* ── Featured post card ── */}
        {featured && (
          <motion.article
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.1, ease: 'easeOut' }}
            className="group relative rounded-3xl overflow-hidden bg-white border border-[#0B1F26]/8 shadow-[0_24px_60px_-30px_rgba(11,31,38,0.35)] mb-20"
            data-testid="blog-featured-card"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Image */}
              <Link
                to={`/blog/${featured.slug}`}
                className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[480px] overflow-hidden"
              >
                <img
                  src={featured.coverImage || featured.thumbnail}
                  alt={featured.title}
                  loading="eager"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                  style={{ objectPosition: featured.coverPosition || 'center' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-[10px] uppercase tracking-[0.22em] text-[#0B1F26]">
                  <Sparkles size={10} className="text-[#D4AF5A]" />
                  Featured
                </span>
              </Link>

              {/* Content */}
              <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5 text-[11px] uppercase tracking-[0.22em] text-[#1E4A55]">
                  <span>{getCategoryByKey(featured.category)?.label || featured.category}</span>
                  {featured.secondaryCategory && (
                    <>
                      <span className="text-[#D4AF5A]">•</span>
                      <span>{featured.secondaryCategory}</span>
                    </>
                  )}
                </div>
                <h2
                  className="text-2xl sm:text-3xl lg:text-[40px] leading-[1.1] mb-4 tracking-[-0.01em] text-[#0B1F26]"
                  style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                >
                  {featured.title}
                </h2>
                <p className="text-[#3F4F55] text-base leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#4A4A4A] mb-8">
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-[#D4AF5A]" />
                    {formatDate(featured.publishedAt)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} className="text-[#D4AF5A]" />
                    {featured.readingTime}
                  </span>
                </div>
                <Link to={`/blog/${featured.slug}`} data-testid="blog-featured-cta">
                  <button
                    className="group/btn inline-flex items-center gap-2 rounded-full px-7 py-3 text-[11px] tracking-[0.22em] uppercase font-medium text-[#0B1F26] transition-all duration-500 hover:shadow-[0_10px_30px_rgba(212,175,90,0.4)] hover:-translate-y-0.5"
                    style={{
                      background:
                        'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                    }}
                  >
                    Read Article
                    <ArrowRight size={13} className="transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </Link>
              </div>
            </div>
          </motion.article>
        )}

        {/* ── Category chips + Grid (only when there are more posts) ── */}
        {hasMorePosts ? (
          <>
            <div className="mb-12" data-testid="blog-category-chips">
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#0B1F26] text-center mb-5">
            Browse by topic
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <button
              type="button"
              onClick={() => setActiveCat('all')}
              data-testid="blog-chip-all"
              className={`px-4 py-2 rounded-full text-[12px] uppercase tracking-[0.18em] transition-all duration-300 border ${
                activeCat === 'all'
                  ? 'bg-[#0B1F26] text-[#E8D9B5] border-[#0B1F26]'
                  : 'bg-white/70 text-[#0B1F26] border-[#0B1F26]/12 hover:border-[#D4AF5A]/70 hover:bg-white'
              }`}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setActiveCat(c.key)}
                data-testid={`blog-chip-${c.key}`}
                className={`px-4 py-2 rounded-full text-[12px] uppercase tracking-[0.18em] transition-all duration-300 border flex items-center gap-1.5 ${
                  activeCat === c.key
                    ? 'bg-[#0B1F26] text-[#E8D9B5] border-[#0B1F26]'
                    : 'bg-white/70 text-[#0B1F26] border-[#0B1F26]/12 hover:border-[#D4AF5A]/70 hover:bg-white'
                }`}
              >
                <span>{c.emoji}</span>
                <span>{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Posts grid ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCat}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7"
            data-testid="blog-grid"
          >
            {otherPosts.length === 0 && (
              <div className="col-span-full text-center py-16 text-[#4A4A4A] text-sm">
                More stories coming soon in this category.
              </div>
            )}
            {otherPosts.map((p) => {
              const cat = getCategoryByKey(p.category);
              const Card = (
                <article
                  className="group rounded-2xl overflow-hidden bg-white border border-[#0B1F26]/8 hover:border-[#D4AF5A]/55 shadow-[0_10px_30px_-20px_rgba(11,31,38,0.3)] hover:shadow-[0_22px_50px_-25px_rgba(11,31,38,0.4)] transition-all duration-500 h-full flex flex-col"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#E8EEEF]">
                    {p.thumbnail && (
                      <img
                        src={p.thumbnail}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
                    {p.comingSoon ? (
                      <span className="absolute top-4 left-4 inline-flex items-center px-3 py-1 rounded-full bg-[#0B1F26]/85 backdrop-blur-md text-[9px] uppercase tracking-[0.22em] text-[#E8D9B5]">
                        Coming Soon
                      </span>
                    ) : (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/90 backdrop-blur-md text-[9px] uppercase tracking-[0.22em] text-[#0B1F26]">
                        {cat?.emoji} <span>{cat?.label}</span>
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3
                      className="text-lg sm:text-xl leading-snug text-[#0B1F26] mb-2.5 tracking-[-0.005em]"
                      style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                    >
                      {p.title}
                    </h3>
                    <p className="text-[14px] leading-relaxed text-[#3F4F55] mb-5 flex-1">
                      {p.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-[#0B1F26]/8">
                      <div className="flex items-center gap-3 text-[11px] text-[#4A4A4A]">
                        <span className="flex items-center gap-1">
                          <Calendar size={11} className="text-[#D4AF5A]" />
                          {formatDate(p.publishedAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={11} className="text-[#D4AF5A]" />
                          {p.readingTime}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-[0.2em] flex items-center gap-1 transition-colors ${
                          p.comingSoon
                            ? 'text-[#888888]'
                            : 'text-[#0B1F26] group-hover:text-[#D4AF5A]'
                        }`}
                      >
                        {p.comingSoon ? 'Soon' : 'Read more'}
                        {!p.comingSoon && (
                          <ArrowRight
                            size={11}
                            className="transition-transform group-hover:translate-x-1"
                          />
                        )}
                      </span>
                    </div>
                  </div>
                </article>
              );
              return p.comingSoon ? (
                <div key={p.slug} aria-disabled className="cursor-default opacity-90">
                  {Card}
                </div>
              ) : (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="block">
                  {Card}
                </Link>
              );
            })}
          </motion.div>
        </AnimatePresence>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="mt-4 mb-2 rounded-3xl border border-[#0B1F26]/10 bg-white/70 backdrop-blur-sm px-8 sm:px-12 py-12 sm:py-14 text-center"
            data-testid="blog-empty-state"
          >
            <p
              className="text-[11px] uppercase tracking-[0.5em] mb-4"
              style={{ color: '#D4AF5A' }}
            >
              More stories on the way
            </p>
            <h3
              className="text-2xl sm:text-3xl text-[#0B1F26] mb-3 tracking-[-0.005em]"
              style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
            >
              The next chapter is being written.
            </h3>
            <p className="text-[#3F4F55] text-base leading-relaxed max-w-xl mx-auto">
              New articles on home décor, ocean inspiration, and the craft behind each piece are
              coming soon. In the meantime, explore the collection or commission something made
              uniquely for you.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/featured"
                className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
              >
                Explore Collection <ArrowRight size={12} />
              </Link>
              <span className="text-[#D4AF5A]">•</span>
              <Link
                to="/contact"
                className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
              >
                Custom Commission <ArrowRight size={12} />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
